import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ExternalLink, Lock, Star } from 'lucide-react';
import SchemaModal from './SchemaModal';
import BottomSheet from './BottomSheet';
import CardImage from './CardImage';
import CardViewer from './CardViewer';
import DeckPicker from './DeckPicker';
import SectionHeader from './SectionHeader';
import EmptyState from './EmptyState';
import Skeleton from './Skeleton';
import Badge from './Badge';
import ButtonSpinner from './ButtonSpinner';
import ShowMore from './ShowMore';
import ManaSymbols from './ManaSymbols';
import FilterStrip from './FilterStrip';
import useGridColumns from './useGridColumns';
import { useIsMobileViewport } from './breakpoints';
import {
  SECTIONS, RARITY_LABELS, formatEuro, cardFaces, hasGermanRulesText, imageColumnWidth,
  buildPrintBlocks, languageCode, conditionCode,
} from './cardDetailGrouping';
import { hasLandscapeView, isLandscape } from './cardOrientation';
import './CardDetailModal.css';

/**
 * Baustein — CardDetailModal.
 * Karten-Modal mit festem Grundaufbau — an jedem Aufrufort gleich — und EINER kontextabhängigen
 * Aktion. Spec: docs/superpowers/specs/2026-10-11-card-detail-modal-redesign-design.md.
 *
 * Reine Darstellung: `detail` ist der Umschlag von GET /api/cards/oracle/:oracleId/detail,
 * `printsList` die seitenweise geladene Print-Liste (GET …/prints) samt Suchbegriff; beides und
 * alle Callbacks kommen vom Aufrufer (components/CardDetail.js). ui/ holt keine Daten.
 *
 * Aufbau Desktop (> 900px): SchemaModal size="xl" (fast bildfüllend), Kopf nur mit Namen.
 * Links: Bild, Block „Dieser Print“ mit Überschrift (Set · Nummer · Seltenheit, Set-Name, Datum,
 * Preis; mobil ohne Überschrift im Kopf), externe Links, Aktions-Slot. Die Bildbreite folgt der
 * Höhe, die unter Print-Infos, Links und Aktion bleibt (imageColumnWidth, per ResizeObserver
 * gemessen): × 5/7, höchstens 360 px, mindestens 200 px; die Spalte ist so breit wie das Bild, so
 * bleibt auch auf 1366×768 alles links inklusive Aktion ohne Scrollen sichtbar. Bild, „Dieser
 * Print“ und Links liegen in einem eigenen Scrollbereich: reicht selbst die Mindestbreite nicht
 * (niedrige Fenster, hohe Skalierung), scrollt nur er senkrecht; der Aktions-Slot steht fest
 * darunter und ist immer sichtbar. Link-Knöpfe kürzen ihren Text mit „…“, nie breiter als die Spalte.
 * Rechts Sprung-Chips (Knöpfe mit aria-pressed) über einer Scroll-Spalte mit den Abschnitten in
 * fester Reihenfolge (SECTIONS): Karte · Alle Prints · Im Besitz · Virtuelle Decks · Freunde.
 * Anzahlen stehen hinter der Überschrift (SectionHeader `count`): Alle Prints · N (sobald `total`
 * bekannt ist), Im Besitz · Summe der Stück, Regeln · N.
 * „Karte“: links ein umrandeter Block je Kartenseite (cardFaces) wie auf der Karte — Name und
 * Kosten in einer Zeile, Typzeile, Trennlinie, Regeltext, Trennlinie, Stärke/Widerstand
 * rechtsbündig; ohne Regeltext entfällt der Textbereich samt Linie, ohne Werte die Fußzeile.
 * Daneben beschriftete Werte als <dl> (Farbidentität, Commander, Standard, EDHREC-Rang), solange
 * der Abschnitt breit genug ist (ab ca. 760 px: Mindestbreite der Blöcke + Abstand + Werte); sonst
 * stehen sie darunter (flex-wrap). Darunter Regeln, drei sichtbar. Scroll-Spy: aktiv ist der
 * letzte Abschnitt, dessen Oberkante den Rand des Scroll-Containers passiert hat.
 *
 * Aufbau Mobil (≤ 900px): BottomSheet size="tall"; Kopf mit Miniatur (Tipp → CardViewer, sofern
 * `thumbnailOpensViewer`), „Dieser Print“ und Links; Chips sticky; Abschnitte im Fluss — der
 * Sheet-Body scrollt (seine Zieh-Heuristik liest dessen scrollTop); Aktions-Slot sticky unten.
 * „Karte“ hat dieselbe Reihenfolge wie am Desktop: Kartenblöcke, darunter die beschrifteten Werte.
 *
 * Verhalten:
 * - Der gezeigte Print wird als Objekt gehalten: Start ist der Print des Aufrufers
 *   (`currentScryfallId`, aus `detail.referencedPrints`). Ist er (noch) nicht auflösbar — etwa
 *   solange der Umschlag des vorigen Prints als Platzhalter steht —, zeigt das Bild `fallback`;
 *   ohne Aufrufer-Print (oder ohne dessen Bild) der erste Print der ungefilterten Liste. Klick auf
 *   eine Kachel in „Im Besitz“, „Alle Prints“, der Vollansicht oder „Freunde“ wechselt ihn; ein
 *   Suchbegriff nie. Ein Auswahlrahmen: „wird links angezeigt“. Ein neuer `currentScryfallId` bei
 *   offenem Modal (z. B. nach „Diesen Print verwenden“) setzt nur die Auswahl zurück.
 * - Im Besitz / Freunde: ein Block je Print (buildPrintBlocks) mit „In Bindern“ / „In Decks“,
 *   Zeilen Anzahl · Foil-Stern (Etched mit „E“) · Sprache · Zustand · Preis · Binder; Freunde mit
 *   dem Namen als erster Spalte; je Liste drei Zeilen, Rest hinter „mehr“. Ist die Zeilenliste
 *   schmaler als ca. 424 px (Container-Query, nicht Gerät), stehen Besitzer- und Bindername in
 *   je einer eigenen Zeile. Physische Decks haben keine Links — die Sammlung kennt keine Adresse
 *   für einen einzelnen Binder.
 * - Alle Prints: genau zwei volle Reihen (useGridColumns), Kacheln ohne Text, „×N“ = Besitz; das
 *   Skeleton füllt dieselben zwei Reihen; „Alle N anzeigen“ → Vollansicht. Ein Print: „Nur ein
 *   Print.“; kein aktueller Print: „Keine aktuellen Prints.“
 * - Rechte Seite hat einen Tausch-Zustand `panel`: 'sections' | 'picker' | 'all-prints';
 *   zurückgesetzt wird er nur beim Öffnen und Schließen.
 *   Vollansicht: „‹ Zurück“, Suchfeld (Set-Kürzel/-Name, an `printsList.onQueryChange`),
 *   Endlos-Scroll per Sentinel (`printsList.loadMore`); scheitert eine Folgeseite, bleiben die
 *   geladenen Kacheln stehen, darunter „Erneut versuchen“ (`printsList.onRetry`, solange ein Laden
 *   läuft gesperrt). Der Wechsel in die Vollansicht scrollt den Scroll-Container nach oben;
 *   „‹ Zurück“ fährt „Alle Prints“ an. Wer die Vollansicht verlässt — gleich auf welchem Weg
 *   (Zurück, Picker, Schließen) —, leert die Suche (`printsList.onQueryChange('')`).
 * - Sprünge (Chip-Klick weich; Direkteinstieg und Rücksprung aus der Vollansicht sofort) scrollen
 *   ausschließlich den Scroll-Container der Abschnitte (scrollToSection).
 * - `section` = Direkteinstieg: der Abschnitt wird nach dem Öffnen angefahren.
 * - `context`:
 *   'add-to-deck' → Slot „Zu Deck hinzufügen“; Klick zeigt statt der Abschnitte den DeckPicker
 *   (nur die vom Aufrufer gereichten Decks), „‹ Zurück“ oben; der Slot wird zu „Hinzufügen“
 *   (aktiv nach Auswahl) → addToDeck.onConfirm(deckId, scryfallId) mit dem links gezeigten
 *   Print → bei true zurück zu den Abschnitten.
 *   'switch-print' → Slot „Diesen Print verwenden“ (aktiv, wenn ein anderer Print gewählt ist,
 *   auch ein veralteter aus dem Besitz) → onSwitchPrint(newScryfallId). Bei `printLocked`
 *   darunter „Print festgelegt · Freigeben“ → onUnlockPrint().
 *   null → Slot leer. `canAct=false` blendet die Aktion aus (kein Bearbeitungsrecht).
 * - Kartenblöcke: Mana-Symbole als Bilder (ManaSymbols, ManaSymbols.Text); EN/DE-Umschalter über
 *   den Blöcken wirkt auf Name, Typzeile und Text, erscheint nur mit deutschem Regeltext;
 *   Startsprache folgt `card.viewerLang`.
 * - Fehler (`error`): Kopf und Bild aus `fallback`, Inhaltsbereich zeigt EmptyState mit
 *   „Erneut versuchen“ (onRetry). `loading`: Skeleton-Zeilen. Print-Liste lädt/scheitert
 *   für sich, der Rest bleibt bedienbar.
 */
const SECTION_DOM_ID = (id) => `card-detail-section-${id}`;

// Nächster scrollender Vorfahr (Desktop: .card-detail__body selbst; mobil: der Sheet-Body).
function nearestScrollParent(el) {
  let node = el;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

// Fährt einen Abschnitt an, indem ausschließlich sein Scroll-Container scrollt (Desktop
// .card-detail__body, mobil der Sheet-Body). Nicht über die Element-Funktion „scroll into view“:
// die scrollt jeden scrollbaren Vorfahren mit, auch den Modal-Body (overflow: hidden), und
// verschiebt so das ganze Modal.
// Ziel ist die Abschnitts-Oberkante relativ zum Container minus scroll-margin-top des Abschnitts.
function scrollToSection(id, behavior) {
  const el = document.getElementById(SECTION_DOM_ID(id));
  if (!el) return false;
  const scroller = nearestScrollParent(el);
  if (scroller) {
    const margin = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
    const offset = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top - scroller.clientTop;
    scroller.scrollTo({ top: scroller.scrollTop + offset - margin, behavior });
  }
  return true;
}

// Aktiv ist der LETZTE Abschnitt, dessen Oberkante die Oberkante des Scroll-Containers passiert
// hat — nicht „der erste sichtbare“: am Scroll-Ende sind mehrere Abschnitte sichtbar, und ein
// Chip-Sprung zum letzten muss den letzten markieren. Ein Scroll-Listener statt
// IntersectionObserver, weil der nur bei Sichtbarkeits-WECHSELN feuert, nicht beim Passieren der
// Oberkante. `enabled` wechselt auch beim Picker-Umweg (Abschnitte werden neu gemountet). Gemessen
// wird gegen die Content-Box-Kante (Border-Box-Oberkante + padding-top): der letzte Abschnitt füllt
// den Container per CSS auf Content-Box-Höhe auf und erreicht beim maximal möglichen Scroll darum
// nur diese Kante, nie die Border-Box-Oberkante — der Toleranzwert 8 deckt den vollen padding-top
// des Containers sonst nicht ab.
function useScrollSpy(containerRef, enabled) {
  const [active, setActive] = useState(SECTIONS[0].id);
  useEffect(() => {
    if (!enabled || !containerRef.current) return undefined;
    const scope = containerRef.current;
    const scroller = nearestScrollParent(scope);
    if (!scroller || scroller.clientHeight === 0) return undefined; // jsdom: kein Layout, kein Spy
    const compute = () => {
      const { top } = scroller.getBoundingClientRect();
      const rootTop = top + parseFloat(window.getComputedStyle(scroller).paddingTop || '0');
      let current = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const el = scope.querySelector(`#${SECTION_DOM_ID(s.id)}`);
        if (el && el.getBoundingClientRect().top - rootTop <= 8) current = s.id;
      }
      setActive(current);
    };
    compute();
    scroller.addEventListener('scroll', compute, { passive: true });
    return () => scroller.removeEventListener('scroll', compute);
  }, [containerRef, enabled]);
  return [active, setActive];
}

function ExternalLinks({ links }) {
  const items = [
    ['Scryfall', links?.scryfall], ['Cardmarket', links?.cardmarket], ['EDHREC', links?.edhrec], ['Tagger', links?.tagger],
  ].filter(([, href]) => !!href);
  if (items.length === 0) return null;
  return (
    <div className="card-detail__links">
      {items.map(([label, href]) => (
        <a key={label} className="card-detail__link" href={href} target="_blank" rel="noopener noreferrer">
          <span className="card-detail__link-text">{label}</span> <ExternalLink size={12} aria-hidden />
        </a>
      ))}
    </div>
  );
}

// Direkteinstieg fährt den Ziel-Abschnitt einmal an. SchemaModal/BottomSheet portalen ihren
// Inhalt über Radix (`@radix-ui/react-portal`), das seinen Container erst in einem eigenen
// Layout-Effekt setzt — ein Layout-Effekt in CardDetailModal selbst läuft VOR diesem
// verschachtelten Mount und findet den Abschnitt noch nicht. Als Kind des Portal-Inhalts
// entsteht diese Komponente dagegen erst, wenn Radix bereits gemountet hat; ihr eigener,
// abhängigkeitsloser Layout-Effekt sieht den fertigen DOM-Baum und wiederholt sich bei jedem
// Re-Render, bis `jumpedRef` den Sprung als erledigt markiert (z.B. sobald `detail` nachlädt).
// Ist `section` bereits der erste Abschnitt (SECTIONS[0], der Default), entfällt der Sprung
// komplett: Mobil liegt der Kopf (Miniatur, „Dieser Print“, Links) im SELBEN Scroll-Container
// wie die Abschnitte (der Sheet-Body) VOR dem ersten Abschnitt — ein Sprung auf den ersten
// Abschnitt scrollt den Kopf dadurch aus dem sichtbaren Bereich, obwohl der natürliche
// Startzustand (scrollTop 0, Kopf sichtbar) bereits exakt das gewünschte Ergebnis ist.
function DirectEntryScroll({ section, jumpedRef, setActiveChip }) {
  useLayoutEffect(() => {
    if (!section || jumpedRef.current) return;
    if (section === SECTIONS[0].id) { jumpedRef.current = true; return; }
    if (scrollToSection(section, 'instant')) { setActiveChip(section); jumpedRef.current = true; }
  });
  return null;
}

// Bildspalte (Desktop): die Bildbreite folgt der Höhe, die unter dem Bild bleibt. Der untere Teil
// ändert seine Höhe mit dem gezeigten Print (zweizeiliger Set-Name, Freigeben-Zeile, kein
// Aktions-Slot) — deshalb Messung per ResizeObserver statt fester vh-Formel. Wird die Spalte
// schmaler und bricht dadurch ein Set-Name um, misst der Observer erneut; die Folge der Breiten
// ist monoton und kommt zur Ruhe. Eigene Komponente, weil der Portal-Inhalt erst nach den
// Layout-Effekten von CardDetailModal im DOM steht (siehe DirectEntryScroll).
// Aufbau: `children` (Bild, „Dieser Print“, Links) liegen in einem Scrollbereich, der erst bei
// Platzmangel unterhalb der Mindestbreite scrollt; `actions` steht fest darunter und bleibt immer
// sichtbar. Der Innenabstand des Scrollbereichs wird von seinem negativen Außenabstand aufgehoben
// und kostet darum keine Höhe — gemessen wird gegen die Spalte selbst.
// `wide`: Karte mit Querdarstellung — die Spalte ist von Anfang an das Hochkant-Maß × 7/5, damit
// der Inhalt rechts beim Umdrehen eines Battles oder Drehen einer Aftermath-Karte nicht springt.
function ImageColumn({ children, actions, wide = false }) {
  const columnRef = useRef(null);
  const scrollRef = useRef(null);
  const [width, setWidth] = useState(null);
  const hasActions = !!actions;
  useLayoutEffect(() => {
    const column = columnRef.current;
    const scroll = scrollRef.current;
    if (!column || !scroll) return undefined;
    const foot = [...scroll.children].slice(1); // alles unter dem Bild im Scrollbereich
    const actionsEl = hasActions ? scroll.nextElementSibling : null;
    const measure = () => {
      const columnGap = parseFloat(window.getComputedStyle(column).rowGap) || 0;
      const scrollGap = parseFloat(window.getComputedStyle(scroll).rowGap) || 0;
      setWidth(imageColumnWidth({
        columnHeight: column.clientHeight,
        lowerHeight: foot.reduce((sum, el) => sum + el.offsetHeight, 0) + (actionsEl?.offsetHeight ?? 0),
        gaps: scrollGap * foot.length + (actionsEl ? columnGap : 0),
        landscape: wide,
      }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    [column, ...foot, actionsEl].filter(Boolean).forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [hasActions, wide]);
  return (
    <aside className="card-detail__image-col" ref={columnRef} data-wide={wide ? 'true' : undefined} style={width ? { '--card-detail-image-col': `${width}px` } : undefined}>
      <div className="card-detail__image-scroll" ref={scrollRef}>{children}</div>
      {actions}
    </aside>
  );
}

function Section({ id, label, count = null, children }) {
  return (
    <section id={SECTION_DOM_ID(id)} data-section={id} className="card-detail__section">
      <SectionHeader size="sm" count={count}>{label}</SectionHeader>
      {children}
    </section>
  );
}

function CardFaces({ card }) {
  // Umschalter nur bei deutschem REGELTEXT: viele Lokalisierungen tragen nur Name/Typenzeile
  // (Standardländer, textlose Karten); der Umschalter gehört zum Regeltext.
  const hasDe = hasGermanRulesText(card);
  // Startsprache = Kartensprache der Nutzer-Präferenz (viewerLang), sofern es deutschen Regeltext gibt.
  const [lang, setLang] = useState(hasDe && card.viewerLang === 'de' ? 'de' : 'en');
  const faces = cardFaces(card, lang);
  return (
    <div className="card-detail__faces">
      {hasDe && (
        <div className="card-detail__lang" role="group" aria-label="Sprache des Regeltexts">
          {['en', 'de'].map((l) => (
            <button key={l} type="button" className={`card-detail__chip${lang === l ? ' card-detail__chip--on' : ''}`} aria-pressed={lang === l} onClick={() => setLang(l)}>{l.toUpperCase()}</button>
          ))}
        </div>
      )}
      {faces.map((f, i) => (
        <div key={i} className="card-detail__face">
          <div className="card-detail__face-head">
            <div className="card-detail__face-title">
              <span className="card-detail__face-name">{f.name}</span>
              {f.manaCost && <ManaSymbols manaCost={f.manaCost} size={16} />}
            </div>
            {f.typeLine && <div className="card-detail__face-type">{f.typeLine}</div>}
          </div>
          {f.text && <p className="card-detail__face-text"><ManaSymbols.Text text={f.text} /></p>}
          {f.stats && <div className="card-detail__face-foot">{f.stats}</div>}
        </div>
      ))}
    </div>
  );
}

function CardFacts({ card }) {
  const colors = card.colorIdentity ?? [];
  return (
    <dl className="card-detail__facts">
      <dt>Farbidentität</dt>
      <dd>{colors.length > 0 ? <ManaSymbols manaCost={colors.map((c) => `{${c}}`).join('')} size={16} /> : 'farblos'}</dd>
      <dt>Commander</dt>
      <dd>{card.legal?.commander ? 'legal' : 'nicht legal'}</dd>
      <dt>Standard</dt>
      <dd>{card.legal?.standard ? 'legal' : 'nicht legal'}</dd>
      {card.edhrecRank != null && (
        <>
          <dt>EDHREC-Rang</dt>
          <dd>#{card.edhrecRank.toLocaleString('de-DE')}</dd>
        </>
      )}
    </dl>
  );
}

const RULINGS_COLLAPSED_COUNT = 3;

function Rulings({ rulings }) {
  const [expanded, setExpanded] = useState(false);
  if (!rulings || rulings.length === 0) return <p className="card-detail__muted">Keine Regeln.</p>;
  const visible = expanded ? rulings : rulings.slice(0, RULINGS_COLLAPSED_COUNT);
  return (
    <>
      <ul className="card-detail__rulings">
        {visible.map((r, i) => (
          <li key={`${r.publishedAt}-${i}`}>
            <ManaSymbols.Text text={r.comment} />
            <span className="card-detail__date">{r.publishedAt}</span>
          </li>
        ))}
      </ul>
      {rulings.length > visible.length && (
        <ShowMore onClick={() => setExpanded(true)}>{`Alle ${rulings.length} anzeigen`}</ShowMore>
      )}
    </>
  );
}

function PrintTile({ print, shown, onShow }) {
  const label = `${print.setName || (print.set || '').toUpperCase()} #${print.collectorNumber ?? '?'}${print.releasedAt ? ` · ${print.releasedAt.slice(0, 4)}` : ''}`;
  return (
    <div className={`card-detail__print${shown ? ' card-detail__print--shown' : ''}`}>
      {/* Kein CardImage in einem <button> (Master-Invariante): gestrecktes Geschwister-Klickziel wie CardTile. */}
      <button type="button" className="card-detail__print-click" onClick={() => onShow(print)} aria-label={label} aria-pressed={shown} />
      <CardImage src={print.imageUrl} alt="">
        {print.owned > 0 && <span className="card-detail__owned">×{print.owned}</span>}
      </CardImage>
    </div>
  );
}

const PRINTS_COLLAPSED_ROWS = 2;

// Eigene Komponente, damit `useGridColumns` erst misst, wenn das Raster wirklich gerendert wird —
// sein Messeffekt läuft nur beim Mount.
function PrintsGrid({ items, total, shownScryfallId, onShow, onShowAll }) {
  const ref = useRef(null);
  const columns = useGridColumns(ref);
  const visible = columns > 0 ? items.slice(0, columns * PRINTS_COLLAPSED_ROWS) : items;
  return (
    <>
      <div className="card-detail__prints" ref={ref}>
        {visible.map((p) => <PrintTile key={p.scryfallId} print={p} shown={p.scryfallId === shownScryfallId} onShow={onShow} />)}
      </div>
      {total > visible.length && <ShowMore onClick={onShowAll}>{`Alle ${total} anzeigen`}</ShowMore>}
    </>
  );
}

// Platzhalter im selben Raster (gleiche CSS-Klasse, gleiche Spaltenzahl): zwei volle Reihen wie
// das fertige Raster, damit das Nachladen nichts verschiebt.
function PrintsSkeleton() {
  const ref = useRef(null);
  const columns = useGridColumns(ref);
  return (
    <div className="card-detail__prints" ref={ref}>
      {Array.from({ length: Math.max(columns, 1) * PRINTS_COLLAPSED_ROWS }, (_, i) => (
        <div key={i} className="card-detail__print-skeleton">
          <Skeleton width="100%" height="100%" label={i === 0 ? 'Prints werden geladen' : undefined} />
        </div>
      ))}
    </div>
  );
}

function PrintsPreview({ printsList, shownScryfallId, onShow, onShowAll }) {
  const { items, total, loading, error, onRetry } = printsList;
  if (loading) return <PrintsSkeleton />;
  if (error && items.length === 0) {
    return <EmptyState variant="error" title="Prints konnten nicht geladen werden." action={<button type="button" className="btn-secondary" onClick={onRetry}>Erneut versuchen</button>} />;
  }
  if (total === 0) return <p className="card-detail__muted">Keine aktuellen Prints.</p>;
  if (total === 1) return <p className="card-detail__muted">Nur ein Print.</p>;
  return <PrintsGrid items={items} total={total} shownScryfallId={shownScryfallId} onShow={onShow} onShowAll={onShowAll} />;
}

function AllPrintsView({ printsList, shownScryfallId, onShow, onBack }) {
  const sentinelRef = useRef(null);
  const { items, total, hasMore, loading, loadingMore, error, loadMore, onRetry, query, onQueryChange } = printsList;
  // Endlos-Scroll: Wurzel ist der nächste scrollende Vorfahr (Desktop .card-detail__body, mobil der
  // Sheet-Body). Nach jeder geladenen Seite neu beobachten — steht der Sentinel dann noch im Blick,
  // feuert der Observer erneut und lädt die nächste.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || error || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) loadMore(); },
      { root: nearestScrollParent(el), rootMargin: '400px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, error, loadMore, items.length]);

  const retry = <button type="button" className="btn-secondary" disabled={loadingMore} onClick={onRetry}>Erneut versuchen</button>;
  let content;
  if (loading) content = <Skeleton height={140} label="Prints werden geladen" />;
  else if (error && items.length === 0) content = <EmptyState variant="error" title="Prints konnten nicht geladen werden." action={retry} />;
  else if (items.length === 0) content = <EmptyState title={query.trim() ? `Kein Print passt zu „${query.trim()}“.` : 'Keine aktuellen Prints.'} />;
  else content = (
    <div className="card-detail__prints">
      {items.map((p) => <PrintTile key={p.scryfallId} print={p} shown={p.scryfallId === shownScryfallId} onShow={onShow} />)}
    </div>
  );

  return (
    <div className="card-detail__all-prints">
      <div className="card-detail__all-prints-head">
        <button type="button" className="btn-secondary card-detail__back" onClick={onBack}><ArrowLeft size={14} aria-hidden /> Zurück</button>
        <SectionHeader size="sm" count={total}>Alle Prints</SectionHeader>
      </div>
      <FilterStrip mobileDock={false}>
        <FilterStrip.Search value={query} onChange={onQueryChange} placeholder="Set-Kürzel oder Set-Name…" />
      </FilterStrip>
      {content}
      {error && items.length > 0 && (
        <div className="card-detail__load-error"><span className="card-detail__muted">Weitere Prints konnten nicht geladen werden.</span>{retry}</div>
      )}
      {loadingMore && <Skeleton height={18} label="Weitere Prints werden geladen" />}
      <div ref={sentinelRef} aria-hidden />
    </div>
  );
}

const LINES_COLLAPSED_COUNT = 3;

function LineList({ title, lines, withOwner }) {
  const [expanded, setExpanded] = useState(false);
  if (lines.length === 0) return null;
  const visible = expanded ? lines : lines.slice(0, LINES_COLLAPSED_COUNT);
  return (
    <div className="card-detail__lines">
      <div className="card-detail__lines-title">{title}</div>
      {visible.map((l) => (
        <div key={l.key} className={`card-detail__line${withOwner ? ' card-detail__line--owner' : ''}`}>
          {withOwner && <span className="card-detail__line-owner" title={l.ownerName}>{l.ownerName}</span>}
          <span className="card-detail__line-qty">{l.quantity}×</span>
          <span className="card-detail__line-foil">
            {l.foil !== 'normal' && <Star size={12} fill="currentColor" role="img" aria-label={l.foil === 'etched' ? 'Etched' : 'Foil'} />}
            {l.foil === 'etched' && <span aria-hidden>E</span>}
          </span>
          <span>{languageCode(l.language)}</span>
          <span>{conditionCode(l.condition)}</span>
          <span className="card-detail__line-price">{formatEuro(l.price)}</span>
          <span className="card-detail__line-binder" title={l.binderName}>{l.binderName}</span>
        </div>
      ))}
      {lines.length > visible.length && <ShowMore onClick={() => setExpanded(true)}>mehr</ShowMore>}
    </div>
  );
}

function PrintBlock({ block, shownScryfallId, onShow, withOwner }) {
  const { print } = block;
  const label = `${(print.set || '').toUpperCase()} #${print.collectorNumber ?? '?'}`;
  const shown = print.scryfallId === shownScryfallId;
  return (
    <div className="card-detail__block">
      <div className={`card-detail__print${shown ? ' card-detail__print--shown' : ''}`}>
        {/* Kein CardImage in einem <button> (Master-Invariante): gestrecktes Geschwister-Klickziel wie CardTile. */}
        <button type="button" className="card-detail__print-click" onClick={() => onShow(print)} aria-label={label} aria-pressed={shown} />
        <CardImage src={print.imageUrl} alt="" />
      </div>
      <div className="card-detail__block-body">
        <div className="card-detail__block-title">{label}</div>
        <LineList title="In Bindern" lines={block.binders} withOwner={withOwner} />
        <LineList title="In Decks" lines={block.decks} withOwner={withOwner} />
      </div>
    </div>
  );
}

function Ownership({ blocks, shownScryfallId, onShow }) {
  if (blocks.length === 0) return <p className="card-detail__muted">Nicht in deinem Besitz.</p>;
  return (
    <div className="card-detail__blocks">
      {blocks.map((b) => <PrintBlock key={b.print.scryfallId} block={b} shownScryfallId={shownScryfallId} onShow={onShow} />)}
    </div>
  );
}

function Decks({ decks, onOpenDeck }) {
  if (!decks || decks.length === 0) return <p className="card-detail__muted">In keinem virtuellen Deck.</p>;
  return (
    <div className="card-detail__chips">
      {decks.map((d) => (
        <button key={d.deckId} type="button" className="card-detail__chip" onClick={() => onOpenDeck?.(d.deckId)}>{d.name} <Badge>×{d.quantity}</Badge></button>
      ))}
    </div>
  );
}

function Friends({ blocks, shownScryfallId, onShow }) {
  if (blocks.length === 0) return <p className="card-detail__muted">Kein Freund besitzt diese Karte.</p>;
  return (
    <div className="card-detail__blocks">
      {blocks.map((b) => <PrintBlock key={b.print.scryfallId} block={b} shownScryfallId={shownScryfallId} onShow={onShow} withOwner />)}
    </div>
  );
}

export default function CardDetailModal({
  open, onOpenChange, fallback, detail, loading = false, error = null, onRetry,
  currentScryfallId, section = 'card', context = null, canAct = true,
  printLocked = false, onUnlockPrint, onSwitchPrint, addToDeck, printsList = null,
  onOpenDeck, thumbnailOpensViewer = true,
}) {
  const isMobile = useIsMobileViewport();
  const scrollRef = useRef(null);
  // Die Auswahl gilt nur für den Aufrufer-Print, bei dem sie getroffen wurde: wechselt
  // `currentScryfallId` (andere Karte oder anderer Print), fällt die Anzeige im selben Render auf
  // den Startwert zurück, ohne auf den Reset-Effekt zu warten.
  const [selection, setSelection] = useState(null);
  const shownSelection = selection && selection.forId === (currentScryfallId ?? null) ? selection.print : null;
  const setShownSelection = useCallback(
    (print) => setSelection(print ? { forId: currentScryfallId ?? null, print } : null),
    [currentScryfallId],
  );
  const [panel, setPanel] = useState('sections'); // 'sections' | 'picker' | 'all-prints'
  const [pendingJump, setPendingJump] = useState(null);
  const [pickedDeck, setPickedDeck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  // Sichtbare Seite und Dreh-Zustand des großen Bilds hält das Modal selbst (gesteuert): nur so
  // kennt es die Seite für die Breite des Bild-Containers (Spec 2026-10-31 §6.2), und der
  // Zustand überlebt den Druck-Wechsel, der `src` ändert. Zurück auf vorn/ungedreht beim Öffnen
  // und beim Wechsel der Karte (Oracle-ID aus Umschlag oder fallback), nicht beim Druck-Wechsel.
  const [face, setFace] = useState('front');
  const [rotated, setRotated] = useState(false);
  const cardKey = detail?.card?.oracleId ?? fallback?.oracleId ?? null;
  const [activeChip, setActiveChip] = useScrollSpy(scrollRef, open && !!detail && panel === 'sections');
  // Der Direkteinstieg fährt den Abschnitt genau einmal pro Öffnen an — `detail` ist nach jeder
  // Invalidierung (Hinzufügen, Print-Wechsel) ein neues Objekt und würde sonst erneut springen.
  const jumpedRef = useRef(false);

  useEffect(() => {
    if (open) { setSelection(null); setPanel('sections'); setPickedDeck(null); setFace('front'); setRotated(false); }
    // `viewerOpen` gehört zu CardDetailModal, nicht zum CardViewer selbst — schließt das
    // Modal (gleich aus welchem Grund: reguläres Schließen, Rücknavigation, ein fälschlich
    // durchgeschlagener Backdrop-Dismiss), bleibt ohne diesen Reset ein CardViewer ohne
    // sichtbaren Träger zurück, sobald der Aufrufer `fallback`/`detail` daraufhin leert.
    else { jumpedRef.current = false; setViewerOpen(false); setPanel('sections'); setPickedDeck(null); }
  }, [open]);
  useEffect(() => { setFace('front'); setRotated(false); }, [cardKey]);
  // Ein neuer Aufrufer-Print bei offenem Modal („Diesen Print verwenden“ in Deck-Editor und
  // Wantlist) setzt nur die Auswahl zurück, `panel` nicht — sonst spränge das Modal ungefragt aus
  // der Vollansicht. Ohne den Reset käme eine alte Auswahl zurück, sobald die alte ID wiederkehrt.
  useEffect(() => { setSelection(null); }, [currentScryfallId]);

  // Die einzige Stelle im Modal, die den Suchbegriff leert: jeder Weg aus der Vollansicht heraus (Zurück,
  // Picker, Schließen). Layout-Effekt, damit „Alle Prints“ keinen gemalten Frame lang die Treffer
  // des alten Suchbegriffs zeigt.
  const prevPanelRef = useRef(panel);
  const onQueryChange = printsList?.onQueryChange;
  useLayoutEffect(() => {
    if (prevPanelRef.current === 'all-prints' && panel !== 'all-prints') onQueryChange?.('');
    prevPanelRef.current = panel;
  }, [panel, onQueryChange]);

  const referencedPrints = useMemo(() => detail?.referencedPrints ?? [], [detail]);
  const listItems = useMemo(() => printsList?.items ?? [], [printsList]);
  // Nachschlagetabelle aller bekannten Prints; der gewählte Print wird als Objekt gehalten, damit
  // ein Suchbegriff, der ihn aus der Liste filtert, das Bild links nicht wechselt.
  const printById = useMemo(
    () => new Map([...referencedPrints, ...listItems].map((p) => [p.scryfallId, p])),
    [referencedPrints, listItems],
  );
  // Listen-Fallback: der erste Print der UNGEFILTERTEN Liste, je Karte gemerkt — ein Suchbegriff
  // darf das Bild links nie wechseln. Erst mit Umschlag: kommt die Print-Seite vor dem Umschlag an,
  // springt das Bild sonst vom neuesten Druck auf den Print des Aufrufers.
  const listFallbackRef = useRef(null);
  const detailOracleId = detail?.card?.oracleId ?? null;
  if (detail && listItems.length > 0 && !(printsList?.query ?? '').trim() && listFallbackRef.current?.oracleId !== detailOracleId) {
    listFallbackRef.current = { oracleId: detailOracleId, print: listItems[0] };
  }
  const listFallback = detail && listFallbackRef.current?.oracleId === detailOracleId ? listFallbackRef.current.print : null;
  // Ist der Aufrufer-Print (noch) nicht auflösbar — `detail` kann der Platzhalter-Umschlag des
  // vorigen Prints sein —, ist `fallback` sein Bild und geht dem ersten Listen-Print vor.
  const currentPrint = currentScryfallId ? printById.get(currentScryfallId) ?? null : null;
  const callerImageFirst = !!currentScryfallId && !currentPrint && !!fallback?.imageUrl;
  const shownPrint = shownSelection ?? currentPrint ?? (callerImageFirst ? null : listFallback) ?? null;
  const image = shownPrint?.imageUrl ?? fallback?.imageUrl ?? null;
  const imageBack = shownPrint?.imageUrlBack ?? fallback?.imageUrlBack ?? undefined;
  const name = detail?.card?.name ?? fallback?.name ?? '';
  // Ausrichtung aus dem fallback, bis der Umschlag da ist: sonst spränge das Bild von hochkant
  // auf quer. `wide` = breite Spalte für die ganze Karte; `imageLandscape` = die gezeigte Seite
  // liegt gerade quer (sichtbare Seite oder gedrehte Aftermath-Karte).
  const orientation = detail?.card?.imageOrientation ?? fallback?.imageOrientation;
  const wide = hasLandscapeView(orientation);
  const imageLandscape = isLandscape(orientation, face) || (rotated && orientation === 'aftermath');
  const ownershipBlocks = useMemo(() => buildPrintBlocks(detail?.ownership ?? [], printById), [detail, printById]);
  const friendBlocks = useMemo(() => buildPrintBlocks(detail?.friendOwnership ?? [], printById), [detail, printById]);

  const jumpTo = (id) => {
    scrollToSection(id, 'smooth');
    setActiveChip(id);
  };

  // „‹ Zurück“ aus der Vollansicht: die Abschnitte stehen nach dem Tausch neu im DOM — erst dann
  // lässt sich „Alle Prints“ anfahren.
  useLayoutEffect(() => {
    if (panel !== 'sections' || !pendingJump) return;
    if (scrollToSection(pendingJump, 'instant')) setActiveChip(pendingJump);
    setPendingJump(null);
  }, [panel, pendingJump, setActiveChip]);
  // Die Vollansicht beginnt oben (Zurück, Titel, Suche): der Scroll-Container ist derselbe wie bei den
  // Abschnitten und steht nach „Alle N anzeigen“ weit unten. Mobil scrollt der Sheet-Body, nicht
  // `scrollRef` selbst.
  useLayoutEffect(() => {
    if (panel !== 'all-prints' || !scrollRef.current) return;
    const scroller = nearestScrollParent(scrollRef.current) ?? scrollRef.current;
    scroller.scrollTop = 0;
  }, [panel]);
  const backFromAllPrints = () => { setPanel('sections'); setPendingJump('prints'); };

  const showAction = canAct && !!context && !!detail;
  const canSwitch = showAction && context === 'switch-print' && shownPrint && shownPrint.scryfallId !== currentScryfallId;

  const runSwitch = async () => {
    if (!canSwitch || !onSwitchPrint) return;
    setBusy(true);
    try { await onSwitchPrint(shownPrint.scryfallId); } finally { setBusy(false); }
  };
  const runAdd = async () => {
    if (!pickedDeck || !addToDeck?.onConfirm) return;
    setBusy(true);
    try {
      const ok = await addToDeck.onConfirm(pickedDeck, shownPrint?.scryfallId);
      if (ok) { setPanel('sections'); setPickedDeck(null); }
    } finally { setBusy(false); }
  };

  const actionSlot = showAction && (
    <div className="card-detail__actions">
      {context === 'add-to-deck' && panel !== 'picker' && (
        <button type="button" className="btn-primary" onClick={() => setPanel('picker')}>Zu Deck hinzufügen</button>
      )}
      {context === 'add-to-deck' && panel === 'picker' && (
        <button type="button" className="btn-primary" disabled={!pickedDeck || busy} onClick={runAdd}>
          {busy ? <><ButtonSpinner /> Wird hinzugefügt…</> : 'Hinzufügen'}
        </button>
      )}
      {context === 'switch-print' && (
        <>
          <button type="button" className="btn-primary" disabled={!canSwitch || busy} onClick={runSwitch}>
            {busy ? <><ButtonSpinner /> Wird gewechselt…</> : 'Diesen Print verwenden'}
          </button>
          {printLocked && (
            <div className="card-detail__locked">
              <Lock size={12} aria-hidden /> <span>Print festgelegt</span>
              <button type="button" className="btn-secondary card-detail__unlock" onClick={() => onUnlockPrint?.()}>Freigeben</button>
            </div>
          )}
        </>
      )}
    </div>
  );

  const chips = (
    <div className="card-detail__chips-row" role="group" aria-label="Abschnitte">
      {SECTIONS.map((s) => (
        <button key={s.id} type="button" aria-pressed={activeChip === s.id} className={`card-detail__chip${activeChip === s.id ? ' card-detail__chip--on' : ''}`} onClick={() => jumpTo(s.id)}>{s.label}</button>
      ))}
    </div>
  );

  let body;
  if (error) {
    body = <EmptyState variant="error" title="Kartendetails konnten nicht geladen werden." action={onRetry ? <button type="button" className="btn-secondary" onClick={onRetry}>Erneut versuchen</button> : undefined} />;
  } else if (loading || !detail) {
    body = <div className="card-detail__loading">{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={18} width={`${90 - i * 12}%`} label={i === 0 ? 'Kartendetails werden geladen' : undefined} />)}</div>;
  } else if (panel === 'picker') {
    body = (
      <div className="card-detail__picker">
        <button type="button" className="btn-secondary card-detail__back" onClick={() => { setPanel('sections'); setPickedDeck(null); }}><ArrowLeft size={14} aria-hidden /> Zurück</button>
        <DeckPicker
          decks={addToDeck?.decks ?? null}
          error={addToDeck?.error ?? null}
          onRetry={addToDeck?.onRetry}
          selectedId={pickedDeck}
          onSelect={setPickedDeck}
          disabledIds={addToDeck?.containingDeckIds ?? []}
          disabledLabel="bereits enthalten"
          hint="Physische Decks füllst du über den Import."
          emptyState={<EmptyState title="Du hast noch kein Deck" description="Lege zuerst ein Deck an." action={addToDeck?.emptyAction} />}
        />
      </div>
    );
  } else if (panel === 'all-prints' && printsList) {
    body = <AllPrintsView printsList={printsList} shownScryfallId={shownPrint?.scryfallId} onShow={setShownSelection} onBack={backFromAllPrints} />;
  } else {
    body = (
      <>
        <Section id="card" label="Karte">
          <div className="card-detail__card-grid">
            <CardFaces key={detail.card.oracleId} card={detail.card} />
            <CardFacts card={detail.card} />
          </div>
          <SectionHeader size="sm" count={detail.rulings.length}>Regeln</SectionHeader>
          <Rulings key={detail.card.oracleId} rulings={detail.rulings} />
        </Section>
        <Section id="prints" label="Alle Prints" count={printsList?.total}>
          {printsList && <PrintsPreview printsList={printsList} shownScryfallId={shownPrint?.scryfallId} onShow={setShownSelection} onShowAll={() => setPanel('all-prints')} />}
        </Section>
        <Section id="ownership" label="Im Besitz" count={(detail.ownership ?? []).reduce((s, o) => s + o.quantity, 0)}>
          <Ownership blocks={ownershipBlocks} shownScryfallId={shownPrint?.scryfallId} onShow={setShownSelection} />
        </Section>
        <Section id="decks" label="Virtuelle Decks">
          <Decks decks={detail.virtualDecks} onOpenDeck={onOpenDeck} />
        </Section>
        <Section id="friends" label="Freunde">
          <Friends blocks={friendBlocks} shownScryfallId={shownPrint?.scryfallId} onShow={setShownSelection} />
        </Section>
      </>
    );
  }

  const priceText = (() => {
    if (!shownPrint) return null;
    const { priceEuro, priceEuroFoil } = shownPrint;
    if (priceEuro == null && priceEuroFoil == null) return 'kein Preis';
    if (priceEuro == null) return `Foil ${formatEuro(priceEuroFoil)}`;
    return priceEuroFoil == null ? formatEuro(priceEuro) : `${formatEuro(priceEuro)} · Foil ${formatEuro(priceEuroFoil)}`;
  })();
  // Überschrift nur am Desktop: mobil steht der Block im knappen Kopf neben der Miniatur.
  const printMeta = shownPrint && (
    <div className="card-detail__print-meta">
      {!isMobile && <SectionHeader size="sm">Dieser Print</SectionHeader>}
      <span className="card-detail__print-title">
        {(shownPrint.set || '').toUpperCase()} #{shownPrint.collectorNumber ?? '?'}
        {shownPrint.rarity && ` · ${RARITY_LABELS[shownPrint.rarity] ?? shownPrint.rarity}`}
      </span>
      {shownPrint.setName && <span className="card-detail__muted">{shownPrint.setName}</span>}
      {shownPrint.releasedAt && <span className="card-detail__muted">{shownPrint.releasedAt}</span>}
      <span>{priceText}</span>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {/* outsideDismissGuard: der CardViewer portalt direkt nach document.body, außerhalb von
            Radix' eigenem Layer-Tracking — ohne die Ausnahme sieht Radix jeden Klick darin
            (auch dessen eigenen ✕) als Klick auf den Sheet-Backdrop und schließt das Sheet mit. */}
        <BottomSheet open={open} onOpenChange={onOpenChange} title={name} size="tall" outsideDismissGuard=".card-viewer">
          <div className="card-detail card-detail--mobile">
            <DirectEntryScroll section={section} jumpedRef={jumpedRef} setActiveChip={setActiveChip} />
            <div className="card-detail__mobile-head">
              <div className="card-detail__thumb">
                {thumbnailOpensViewer && (
                  <button type="button" className="card-detail__thumb-click" onClick={() => setViewerOpen(true)} aria-label="Karte groß anzeigen" />
                )}
                {/* ohne backSrc: die Rückseite zeigt der Viewer; ein Flip-Button auf 64 px wäre nicht bedienbar */}
                <CardImage src={image} alt={name} />
              </div>
              <div className="card-detail__mobile-titles">
                {printMeta}
                <ExternalLinks links={shownPrint?.links} />
              </div>
            </div>
            {panel === 'sections' && !error && detail && <div className="card-detail__mobile-chips">{chips}</div>}
            {/* Mobil scrollt der Sheet-Body selbst (seine Zieh-Heuristik liest dessen scrollTop) — kein eigener Scroll-Container */}
            <div className="card-detail__body card-detail__body--flow" ref={scrollRef}>{body}</div>
            {actionSlot && <div className="card-detail__mobile-actions">{actionSlot}</div>}
          </div>
        </BottomSheet>
        {/* raised: der Viewer öffnet AUS dem bereits offenen BottomSheet heraus und muss über
            dessen eigenem Backdrop liegen, nicht darunter (siehe CardViewer-JSDoc). */}
        {thumbnailOpensViewer && (
          <CardViewer open={viewerOpen} cards={[{ imageUrl: image, imageUrlBack: imageBack, name, imageOrientation: orientation }]} index={0} onIndexChange={() => {}} onClose={() => setViewerOpen(false)} label={name} raised />
        )}
      </>
    );
  }

  return (
    <SchemaModal open={open} onOpenChange={onOpenChange} size="xl" title={name}>
      <div className="card-detail">
        <DirectEntryScroll section={section} jumpedRef={jumpedRef} setActiveChip={setActiveChip} />
        <ImageColumn actions={actionSlot || null} wide={wide}>
          {/* data-narrow: hochkant stehende Seite in 5/7 der breiten Spalte, mittig — die Spalte
              bleibt gleich breit, beim Umdrehen oder Drehen ändert sich nur die Höhe */}
          <div className="card-detail__image" data-narrow={wide && !imageLandscape ? 'true' : undefined}>
            <CardImage
              src={image}
              backSrc={imageBack}
              alt={name}
              landscape="always"
              orientation={orientation}
              face={face}
              onFlip={() => setFace((f) => (f === 'back' ? 'front' : 'back'))}
              rotated={rotated}
              onRotate={() => setRotated((r) => !r)}
            />
          </div>
          <div className="card-detail__image-foot">
            {printMeta}
            <ExternalLinks links={shownPrint?.links} />
          </div>
        </ImageColumn>
        <div className="card-detail__main">
          {panel === 'sections' && !error && detail && chips}
          <div className="card-detail__body" ref={scrollRef}>{body}</div>
        </div>
      </div>
    </SchemaModal>
  );
}

CardDetailModal.Section = Section;
CardDetailModal.Rulings = Rulings;
CardDetailModal.Prints = PrintsPreview;
CardDetailModal.Ownership = Ownership;
CardDetailModal.Decks = Decks;
CardDetailModal.Friends = Friends;
